import React from 'react';
import { Tooltip } from 'antd';
import { CheckCircleFilled, ClockCircleOutlined } from '@ant-design/icons';
import { Link } from 'react-admin';
import { formatDateAndTime, formatDateAndTimeFromNow } from '../../helpers/utils';
import { PriceTag } from '../Common/Tags';
import { getCurrency } from '../Common/utils';
import { chargeTypeLabel, scopeOf } from './constants';

const Cell = ({ label, value }: { label: string; value?: React.ReactNode }) => (
    <Tooltip placement="topLeft" title={typeof value === 'string' ? value : undefined}>
        <div className={`chg-cell${value ? '' : ' chg-cell--empty'}`} data-label={label}>
            {value || '—'}
        </div>
    </Tooltip>
);

/**
 * One charge under the shared column header. A charge is a record of something
 * that happened rather than something to open, so the row is not clickable —
 * only the service item it was raised against is a link.
 */
const ChargeRow = ({
    record,
    serviceItemPath,
    showConsumer,
}: {
    record: any;
    serviceItemPath: string;
    /** service owners see whose item was charged; a consumer is always their own */
    showConsumer: boolean;
}) => {
    const currency = getCurrency();
    const { serviceItem, consumer, application } = scopeOf(record);

    return (
        <div className="chg-row">
            <div className="chg-cell chg-cell--id" data-label="ID">
                {record.id}
            </div>

            <Tooltip placement="topLeft" title={serviceItem?.name}>
                <div className="chg-cell" data-label="Service item">
                    {serviceItem?.id ? (
                        <Link className="chg-link" to={`${serviceItemPath}/${serviceItem.id}`}>
                            {serviceItem.name || `#${serviceItem.id}`}
                        </Link>
                    ) : (
                        '—'
                    )}
                </div>
            </Tooltip>

            {showConsumer && <Cell label="Consumer" value={consumer?.name} />}

            <Cell label="Application" value={application?.name} />

            <Cell label="Type" value={chargeTypeLabel(record.charge_type)} />

            <div className="chg-cell chg-cell--amount" data-label="Total">
                <PriceTag amount={record.total_charge || 0} currency={currency} />
            </div>

            {/* a mark rather than a word: the column is read down rather than
                across, and the header already names what it is. The meaning
                stays reachable on hover and to a screen reader. */}
            <div className="chg-cell chg-cell--state" data-label="Processed">
                <Tooltip title={record.processed ? 'Processed' : 'Pending'}>
                    <span className={`chg-state chg-state--${record.processed ? 'done' : 'pending'}`}>
                        {record.processed ? (
                            <CheckCircleFilled aria-hidden="true" />
                        ) : (
                            <ClockCircleOutlined aria-hidden="true" />
                        )}
                        <span className="chg-sr">{record.processed ? 'Processed' : 'Pending'}</span>
                    </span>
                </Tooltip>
            </div>

            <Tooltip title={record.start ? formatDateAndTime(record.start) : undefined}>
                <div className="chg-cell chg-cell--time" data-label="Created">
                    {record.start ? formatDateAndTimeFromNow(record.start) : '—'}
                </div>
            </Tooltip>
        </div>
    );
};

export default ChargeRow;
